import React, { useId, useRef, useState, useEffect, useMemo } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Select - custom-styled dropdown (not native <select> look).
 * @param {string} [label]
 * @param {{label: string, value: string}[]} options
 * @param {string} [value]
 * @param {(value: string) => void} [onChange]
 * @param {string} [placeholder="Select..."]
 * @param {string} [error]
 * @param {boolean} [disabled]
 * @param {boolean} [searchable] - adds a filter input at the top of the menu
 * @param {string} [className]
 * @param {string} [id]
 */
export function Select({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  error,
  disabled = false,
  searchable = false,
  className,
  id,
}) {
  const generatedId = useId();
  const selectId = id || generatedId;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(query.toLowerCase())
    );
  }, [options, query, searchable]);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  function openMenu() {
    if (disabled) return;
    setIsOpen(true);
    const idx = filteredOptions.findIndex((opt) => opt.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }

  function closeMenu() {
    setIsOpen(false);
    setQuery("");
  }

  function selectOption(option) {
    onChange?.(option.value);
    closeMenu();
  }

  function handleTriggerKeyDown(event) {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openMenu();
    }
  }

  function handleListKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (filteredOptions[activeIndex]) selectOption(filteredOptions[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
    }
  }

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={selectId}
          className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => (isOpen ? closeMenu() : openMenu())}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50 dark:disabled:bg-neutral-800",
            error && "border-danger-500 focus-visible:ring-danger-500",
            className
          )}
        >
          <span className={cn(!selectedOption && "text-neutral-400 dark:text-neutral-500")}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-neutral-500 dark:text-neutral-400 transition-transform",
              isOpen && "rotate-180"
            )}
            aria-hidden="true"
          />
        </button>

        {isOpen && (
          <div
            className="absolute z-10 mt-1 w-full overflow-hidden rounded bg-white dark:bg-neutral-900 shadow-popover dark:shadow-black/40"
            onKeyDown={handleListKeyDown}
          >
            {searchable && (
              <div className="relative border-b border-neutral-100 dark:border-neutral-800 p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(0);
                  }}
                  placeholder="Search..."
                  className="h-8 w-full rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 pl-8 pr-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                />
              </div>
            )}
            <ul
              ref={listRef}
              role="listbox"
              tabIndex={-1}
              className="max-h-60 overflow-auto py-1"
            >
              {filteredOptions.length === 0 && (
                <li className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">No options found</li>
              )}
              {filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => selectOption(option)}
                    className={cn(
                      "flex cursor-pointer items-center justify-between px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100",
                      isActive && "bg-neutral-100 dark:bg-neutral-800",
                      isSelected && "bg-primary-50 dark:bg-primary-900/40 text-primary-900 dark:text-primary-300 font-medium"
                    )}
                  >
                    {option.label}
                    {isSelected && <Check className="h-4 w-4" aria-hidden="true" />}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-danger-700 dark:text-danger-500">{error}</p>}
    </div>
  );
}
